import {
  compileJsonSchema,
  SchemaObject,
  ValidateFunction,
} from '@pogokid/schema/dynamic'
import {
  CleanValuesOptions,
  compileValidationSchema,
  ValidateSchemaFunction,
  ValidateSchemaOptions,
  ValidateSchemaResult,
} from '@pogokid/schema'
import { AnyRecord } from '@pogokid/util'
import {
  JSONSchemaStandardProperty,
  SchemaProperty,
} from './types'
import { isJSONSchemaStandardProperty } from './guards'

export interface DynamicSchemaOptions {
  id: string
  jsonSchema: SchemaObject & {
    required?: string[]
    'x-uniqueKeys'?: string[][]
  }
  cleanValuesOptions?: CleanValuesOptions
}

const dbTypeMap = new Map<string, string>([
  ['array', 'jsonb'],
  ['boolean', 'boolean'],
  ['object', 'jsonb'],
  ['date-time', 'timestamptz'],
  ['email', 'text'],
  ['string', 'text'],
  ['number', 'double precision'],
  ['url', 'text'],
  ['uuid', 'uuid'],
  ['integer', 'integer'],
  ['uint8', 'smallint'],
  ['uint32', 'integer'],
  ['uint64', 'bigint'],
])

export class DynamicSchema<T extends AnyRecord> {
  protected compiledJsonSchema: ValidateFunction<T>
  protected options: DynamicSchemaOptions
  private _properties: SchemaProperty[] | undefined

  id: string
  validateFn: ValidateSchemaFunction<T>

  static new<T extends AnyRecord>(
    options: DynamicSchemaOptions
  ) {
    return new DynamicSchema<T>(options)
  }

  constructor(options: DynamicSchemaOptions) {
    this.options = options
    this.id = options.id
    this.compiledJsonSchema = compileJsonSchema(
      options.jsonSchema
    ) as ValidateFunction<T>
    this.validateFn = compileValidationSchema(
      this.id,
      this.compiledJsonSchema,
      this.options.cleanValuesOptions
    )
  }

  propertyKeys(): string[] {
    return Object.keys(this.options.jsonSchema.properties)
  }

  /**
   * The columns that may only hold one row each — the dedupe anchors,
   * as declared by `x-uniqueKeys` on the schema.
   *
   * Each entry is one index; more than one column in an entry means a
   * composite index. Follows Table Schema's `uniqueKeys`, and is what
   * the projection's table creation builds unique indexes from.
   *
   * Declare a key here only when a second row for the same value would
   * mean the same fact recorded twice. A constraint that merely holds
   * on the server is not automatically right for a read model built by
   * replaying events.
   */
  uniqueKeys(): string[][] {
    const declared = this.options.jsonSchema['x-uniqueKeys']
    if (!Array.isArray(declared)) return []
    const known = new Set(this.propertyKeys())
    return declared.filter(
      (columns) =>
        Array.isArray(columns) &&
        columns.length > 0 &&
        // A key naming a column the schema does not have would build
        // an index the table cannot support.
        columns.every((column) => known.has(column))
    )
  }

  /**
   * Whether a column is allowed to hold no value.
   *
   * A null is the absence of a value rather than a value that can
   * collide, so a unique index over a nullable column excludes nulls.
   */
  isNullableProperty(name: string): boolean {
    return (
      this.properties().find(
        (property) => property.name === name
      )?.isNullable ?? false
    )
  }

  properties(): SchemaProperty[] {
    if (this._properties) return this._properties
    const { properties, required } = this.options.jsonSchema
    this._properties = Object.entries(properties)
      .filter(
        (entry): entry is [string, JSONSchemaStandardProperty] =>
          isJSONSchemaStandardProperty(entry[1])
      )
      .map(
        ([name, property]): [
          string,
          JSONSchemaStandardProperty
        ] => {
          if (typeof property['$ref'] === 'string') {
            const ref =
              this.compiledJsonSchema.schemaEnv.refs[
                property['$ref']
              ]
            if (ref) {
              console.log('Resolved ref', property['$ref'], ref)
              return [name, ref as JSONSchemaStandardProperty]
            } else {
              console.warn(
                'Could not resolve $ref',
                property['$ref']
              )
            }
          }
          return [name, property]
        }
      )
      .map(([name, property]): SchemaProperty => {
        const type = Array.isArray(property.type)
          ? property.type[0]
          : property.type
        const dbType = property.format
          ? dbTypeMap.get(property.format)
          : Array.isArray(property.type)
          ? dbTypeMap.get(property.type[0])
          : dbTypeMap.get(property.type)
        if (!dbType) {
          throw new Error(
            `Unknown dbType ${property.format || property.type}`
          )
        }
        return {
          name,
          type,
          dbType,
          format: property.format,
          isNullable: required?.includes(name)
            ? Array.isArray(property.type) &&
              property.type.includes('null')
            : true,
        }
      })
    return this._properties
  }

  propertiesOfType(type: string) {
    return this.properties().filter(
      (property) => property.type === type
    )
  }

  isPartial(
    obj: unknown,
    allowEmpty = false
  ): obj is Partial<T> {
    if (!!obj && typeof obj === 'object') {
      const properties = this.propertyKeys()
      const keys = Object.keys(obj)
      if (!allowEmpty && keys.length === 0) return false
      const hasInvalidKeys = keys.some(
        (k) => !properties.includes(k)
      )
      return !hasInvalidKeys
    }
    return false
  }

  validateMany(
    arr: unknown[],
    options?: ValidateSchemaOptions
  ): ValidateSchemaResult<T[]> {
    const cleanedValues: T[] = []
    for (const data of arr) {
      const result = this.validateFn(data, options)
      if (!result.isValid) {
        return { ...result, cleanedValues: [] }
      } else {
        cleanedValues.push(result.cleanedValues)
      }
    }
    return { isValid: true, cleanedValues }
  }
}
